import { useState } from 'react';
import { TT } from '@features/ai-prompting/terminalTokens';
import SandboxShell from './SandboxShell';
import EvaluationTrace from './EvaluationTrace';
import ResultPanel from './ResultPanel';
import { useRunState, submitLabel } from '@features/ai-prompting/hooks/useRunState';

/**
 * Study — no editor. The work surface is the five-block worked example
 * (spec Part 4/6's ASCII diagram) plus MCQ questions, code-scored only
 * (no LLM call for this format).
 *
 * The seeded `rep.config.example` string uses these five section headers
 * verbatim (confirmed against live rows) — parsed positionally rather
 * than by a data-format change, since two of the five headers are
 * identical text ("WHAT IT PRODUCED" appears after both prompt A and B).
 */
const HEADERS = ['PROMPT A', 'WHAT IT PRODUCED', 'PROMPT B', 'WHAT IT PRODUCED', 'WHAT CHANGED'];

const parseExample = (raw) => {
  if (!raw) return null;
  const blocks = ['', '', '', '', ''];
  let headerIdx = -1;
  for (const line of raw.split('\n')) {
    const trimmed = line.trim();
    if (headerIdx + 1 < HEADERS.length && trimmed === HEADERS[headerIdx + 1]) {
      headerIdx += 1;
      continue;
    }
    if (headerIdx >= 0) blocks[headerIdx] += (blocks[headerIdx] ? '\n' : '') + line;
  }
  if (headerIdx < HEADERS.length - 1) return null;
  const [promptA, outputA, promptB, outputB, whatChanged] = blocks.map(b => b.trim());
  return { promptA, outputA, promptB, outputB, whatChanged };
};

const PromptBlock = ({ label, text, dimmed }) => (
  <div style={{ marginBottom: 2 }}>
    <div style={{ fontFamily: TT.fontMono, fontSize: 10, color: TT.textDim, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
      {label}
    </div>
    <div style={{
      background: TT.bg, border: `1px solid ${TT.border}`, borderRadius: TT.radius.sm,
      padding: '10px 14px', fontFamily: TT.fontMono, fontSize: 13, lineHeight: 1.6,
      color: dimmed ? TT.textMuted : TT.text, opacity: dimmed ? 0.7 : 1,
    }}>
      <span style={{ color: TT.textDim, marginRight: 8, userSelect: 'none' }}>$</span>{text}
    </div>
  </div>
);

const OutputBlock = ({ text }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{ fontFamily: TT.fontMono, fontSize: 10, color: TT.textDim, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
      what it produced
    </div>
    <div style={{
      background: TT.bgElevated, borderRadius: TT.radius.sm, padding: '10px 14px 10px 24px',
      fontFamily: TT.fontSans, fontSize: 13, lineHeight: 1.65, color: TT.textMuted,
    }}>
      {text}
    </div>
  </div>
);

const WorkedExample = ({ example }) => {
  const parsed = parseExample(example);
  if (!parsed) return null;
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontFamily: TT.fontMono, fontSize: 10, color: TT.textDim, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
        annotated example
      </div>
      <PromptBlock label="prompt A · weaker" text={parsed.promptA} dimmed />
      <OutputBlock text={parsed.outputA} />
      <PromptBlock label="prompt B · stronger" text={parsed.promptB} />
      <OutputBlock text={parsed.outputB} />
      <div style={{ borderLeft: `3px solid ${TT.orange}`, paddingLeft: 14 }}>
        <div style={{ fontFamily: TT.fontMono, fontSize: 10, color: TT.textDim, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
          what changed
        </div>
        <p style={{ fontFamily: TT.fontSans, fontSize: 13.5, color: TT.text, lineHeight: 1.65, margin: 0 }}>{parsed.whatChanged}</p>
      </div>
    </div>
  );
};

const QuestionBlock = ({ question, options, index, selected, onSelect, disabled, breakdown }) => {
  const graded = breakdown?.[index];
  return (
    <div style={{ border: `1px solid ${TT.border}`, borderRadius: TT.radius.sm, padding: 14, marginBottom: 12 }}>
      <div style={{ fontFamily: TT.fontSans, fontSize: 13, color: TT.text, marginBottom: 10 }}>{question}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {options.map((opt, oi) => (
          <button
            key={oi}
            onClick={() => !disabled && onSelect(index, oi)}
            disabled={disabled}
            className="ps-focusable"
            style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
              padding: '8px 12px', borderRadius: TT.radius.sm, cursor: disabled ? 'default' : 'pointer',
              background: selected === oi ? `${TT.orange}14` : 'transparent',
              border: `1px solid ${selected === oi ? TT.orange : TT.border}`,
              fontFamily: TT.fontSans, fontSize: 12.5, color: TT.textMuted,
            }}
          >
            <span style={{ color: selected === oi ? TT.orange : TT.textDim }}>{selected === oi ? '●' : '○'}</span>
            {opt}
          </button>
        ))}
      </div>
      {graded && !graded.correct && (
        <p style={{ fontFamily: TT.fontMono, fontSize: 12, color: TT.amber, lineHeight: 1.6, margin: '10px 0 0' }}>
          the correct answer was: {options[graded.correctIndex]}
        </p>
      )}
    </div>
  );
};

export const StudyPanel = ({ rep, lessonName, principle, topBar, accent, onContinue }) => {
  const mcq = rep.config?.mcq ?? [];
  const [answers, setAnswers] = useState(() => Array(mcq.length).fill(null));
  const run = useRunState(rep.rep_id);
  const { phase, result, traceEvents, errorMessage, showCriteria, submit, isLocked, isFailed, isSystemError } = run;

  const selectAnswer = (qIndex, optionIndex) => {
    setAnswers(prev => prev.map((a, i) => (i === qIndex ? optionIndex : a)));
  };

  const submission = { mcqAnswers: answers };
  const canSubmit = answers.every(a => a != null);
  const label = submitLabel(phase, '$ check answers');
  const breakdown = result?.deterministic?.mcqBreakdown;

  const handleSubmitClick = () => {
    if (phase === 'pass') { onContinue?.(); return; }
    if (phase === 'fail' || phase === 'error') { submit(submission); return; }
    if (canSubmit) submit(submission);
  };

  const revealedCriteria = showCriteria && result?.deterministic?.checks
    ? result.deterministic.checks.map(c => ({ name: c.name, passed: c.passed }))
    : null;

  const rail = {
    lessonName,
    principle,
    goal: rep.config?.goal,
    constraints: rep.config?.constraints ?? [],
    dimensions: rep.evaluatedOnDimensions ?? [],
    needsHiddenCases: false,
    revealedCriteria,
  };

  return (
    <SandboxShell
      accent={accent}
      topBar={topBar}
      rail={rail}
      formatLabel={rep.format}
      repTitle={rep.title}
      splitLayout
      stickyLeft
      leftColumn={
        <div>
          <div style={{ fontSize: 12, color: TT.textDim, fontStyle: 'italic', lineHeight: 1.6, marginBottom: 4 }}>{'// instruction'}</div>
          <p style={{ fontFamily: TT.fontSans, fontSize: 13.5, color: TT.text, lineHeight: 1.6, margin: '0 0 20px', maxWidth: 640 }}>{rep.instruction}</p>
          <WorkedExample example={rep.config?.example} />
        </div>
      }
      rightColumn={
        <div>
          {mcq.map((q, i) => (
            <QuestionBlock
              key={i}
              question={q.question}
              options={q.options}
              index={i}
              selected={answers[i]}
              onSelect={selectAnswer}
              disabled={isLocked}
              breakdown={breakdown}
            />
          ))}

          <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <button
              onClick={handleSubmitClick}
              disabled={phase === 'idle' && !canSubmit}
              className="ps-focusable"
              style={{
                padding: '10px 20px', borderRadius: TT.radius.sm, fontFamily: TT.fontMono,
                fontSize: 13, fontWeight: 700, cursor: (phase === 'idle' && !canSubmit) ? 'not-allowed' : 'pointer',
                background: 'transparent',
                color: isFailed ? TT.amber : accent,
                border: `1px solid ${isFailed ? TT.amber : accent}`,
                opacity: (phase === 'idle' && !canSubmit) ? 0.4 : 1,
              }}
            >
              {label}
            </button>
            {isSystemError && (
              <span style={{ fontFamily: TT.fontMono, fontSize: 12, color: TT.cyan }}>⚠ {errorMessage}</span>
            )}
          </div>

          {(run.isRunning || traceEvents.length > 0) && <EvaluationTrace events={traceEvents} />}
          {(phase === 'pass' || phase === 'fail') && <ResultPanel result={result} save={run.save} />}
        </div>
      }
    />
  );
};

export default StudyPanel;
